// src/features/account/api/accountApi.ts
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../config/api';
import useAccountStore, { ApiKey, UsageData } from '../../../store/accountStore';
import useAuthStore from '../../../store/authStore';

// Get user profile
export const fetchUserProfile = async (): Promise<void> => {
  const { setLoading, setError } = useAuthStore.getState();
  
  try {
    setLoading(true);
    const response = await apiClient.get(API_ENDPOINTS.GET_PROFILE);
    
    // Handle both possible API response formats
    const user = response.data.user || response.data;
    useAuthStore.getState().setUser(user);
  } catch (error: any) {
    const errorMessage = error.response?.data?.error || 'Failed to fetch profile. Please try again.';
    setError(errorMessage);
    throw new Error(errorMessage);
  } finally {
    setLoading(false);
  }
};

// Update user profile
export const updateUserProfile = async (profileData: any): Promise<void> => {
  const { setLoading, setError } = useAuthStore.getState();
  
  try {
    setLoading(true);
    const response = await apiClient.put(API_ENDPOINTS.UPDATE_PROFILE, profileData);
    
    // Handle both possible API response formats
    const user = response.data.user || response.data;
    useAuthStore.getState().setUser(user);
  } catch (error: any) {
    const errorMessage = error.response?.data?.error || 'Failed to update profile. Please try again.';
    setError(errorMessage);
    throw new Error(errorMessage);
  } finally {
    setLoading(false);
  }
};

// Get API keys
export const fetchApiKeys = async (): Promise<ApiKey[]> => {
  const { setLoading, setError, setApiKeys } = useAccountStore.getState();
  
  try {
    setLoading(true);
    console.log('Fetching API keys from:', API_ENDPOINTS.GET_API_KEYS);
    
    const response = await apiClient.get(API_ENDPOINTS.GET_API_KEYS);
    console.log('API keys response:', response);
    
    // Handle different response formats
    const apiKeys = response.data.apiKeys || response.data || [];
    console.log('Processed API keys:', apiKeys);
    
    // Ensure apiKeys is an array
    setApiKeys(Array.isArray(apiKeys) ? apiKeys : []);
    return Array.isArray(apiKeys) ? apiKeys : [];
  } catch (error: any) {
    console.error('Error fetching API keys:', error);
    const errorMessage = error.response?.data?.error || 'Failed to fetch API keys. Please try again.';
    setError(errorMessage);
    
    // Return empty array instead of throwing error to prevent component crashes
    setApiKeys([]);
    throw error;
  } finally {
    setLoading(false);
  }
};

// Create new API key
export const createApiKey = async (name: string): Promise<ApiKey> => {
  const { setLoading, setError, addApiKey } = useAccountStore.getState();
  
  try {
    setLoading(true);
    const response = await apiClient.post(API_ENDPOINTS.CREATE_API_KEY, { name });
    const newApiKey = response.data.apiKey;
    addApiKey(newApiKey);
    return newApiKey;
  } catch (error: any) {
    const errorMessage = error.response?.data?.error || 'Failed to create API key. Please try again.';
    setError(errorMessage);
    throw new Error(errorMessage);
  } finally {
    setLoading(false);
  }
};

// Delete API key
export const deleteApiKey = async (id: string): Promise<void> => {
  const { setLoading, setError, removeApiKey } = useAccountStore.getState();
  
  try {
    setLoading(true);
    await apiClient.delete(`${API_ENDPOINTS.DELETE_API_KEY}/${id}`);
    removeApiKey(id);
  } catch (error: any) {
    const errorMessage = error.response?.data?.error || 'Failed to delete API key. Please try again.';
    setError(errorMessage);
    throw new Error(errorMessage);
  } finally {
    setLoading(false);
  }
};

// Get usage statistics
/**
 * Fetch usage statistics
 * @returns {Promise<UsageData>} Usage statistics
 */
export const fetchUsageStats = async (): Promise<UsageData> => {
  const { setLoading, setError, setUsage } = useAccountStore.getState();
  
  try {
    setLoading(true);
    
    console.log('Fetching usage statistics...');
    const response = await apiClient.get(API_ENDPOINTS.GET_USAGE);
    console.log('API Response:', response);
    
    // Check if response contains an error object
    if (response.data && response.data.error) {
      console.error('API returned an error:', response.data.error);
      const errorMsg = typeof response.data.error === 'string' 
        ? response.data.error 
        : response.data.error.message || 'Unknown error from API';
      setError(errorMsg);
      throw new Error(errorMsg);
    }
    
    // Process the response data
    const responseData = response.data;
    console.log('Raw response data:', responseData);
    
    // Extract usage data, handling different response formats
    const usageData = responseData.usage || responseData;
    console.log('Extracted usage data:', usageData);
    
    // Define default values for all expected properties
    const defaultData: UsageData = {
      periodStart: new Date().toISOString(),
      periodEnd: new Date().toISOString(),
      totalUsage: 0,
      dailyUsage: [],
      balance: 0
    };
    
    // Create a properly formatted data object with all required properties
    const formattedData: UsageData = {
      ...defaultData,
      // Only include properties from usageData that are valid (not objects that could cause rendering issues)
      ...(usageData ? Object.fromEntries(
        Object.entries(usageData).filter(([_, value]) => 
          !(typeof value === 'object' && value !== null && !Array.isArray(value))
        )
      ) : {})
    };
    
    // If dailyUsage exists but isn't an array, convert it to the expected format
    if (usageData && usageData.dailyUsage && !Array.isArray(usageData.dailyUsage)) {
      console.warn('dailyUsage is not an array, converting to empty array');
      formattedData.dailyUsage = [];
    }
    
    // Ensure dailyUsage items have the correct structure
    if (Array.isArray(formattedData.dailyUsage)) {
      formattedData.dailyUsage = formattedData.dailyUsage.map(item => ({
        date: typeof item.date === 'string' ? item.date : new Date().toISOString(),
        count: typeof item.count === 'number' ? item.count : 0
      }));
    }
    
    // Ensure balance is a number
    formattedData.balance = typeof formattedData.balance === 'number' ? 
      formattedData.balance : parseFloat(formattedData.balance) || 0;
    
    console.log('Final formatted data:', formattedData);
    setUsage(formattedData);
    return formattedData;
  } catch (error: any) {
    console.error('Error in fetchUsageStats:', error);
    
    // Handle different types of errors
    let errorMessage: string;
    
    if (error.response) {
      // The request was made and the server responded with a non-2xx status code
      console.error('Error response from server:', error.response);
      errorMessage = error.response.data?.error || 
                    (typeof error.response.data === 'string' ? error.response.data : 
                    'Server error: ' + (error.response.status || 'unknown status'));
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received from server');
      errorMessage = 'No response from server. Please check your connection.';
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error setting up request:', error.message);
      errorMessage = error.message || 'An unknown error occurred';
    }
    
    // Ensure errorMessage is a string
    if (typeof errorMessage !== 'string') {
      console.warn('Error message is not a string, converting:', errorMessage);
      try {
        errorMessage = JSON.stringify(errorMessage);
      } catch (e) {
        errorMessage = 'Failed to fetch usage statistics. Please try again.';
      }
    }
    
    setError(errorMessage);
    throw new Error(errorMessage);
  } finally {
    setLoading(false);
  }
};